import React, { useState, useEffect } from "react";
import { createPortal } from 'react-dom';
import { doc, setDoc, deleteDoc, onSnapshot, serverTimestamp, updateDoc } from "firebase/firestore";
import { db, auth } from "../firebase.config";
import { useAuthState } from 'react-firebase-hooks/auth';
import { useAgency } from '../AgencyContext.js';
import "./ManageUsers.css";
import { Lock, ContentCopy, Group, Close, Link } from "@mui/icons-material";

function ManageUsers() {
  const [users, setUsers] = useState([]);
  const [success, setSuccess] = useState("");
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [modalSuccess, setModalSuccess] = useState("");
  const [user] = useAuthState(auth);
  const { currentAgency, getAgencyCollection } = useAgency();

  // Check and add missing Manager role code if needed
  const ensureManagerCode = async () => {
    if (!currentAgency) return;
    
    if (!currentAgency.roleCodes?.Manager) {
      try {
        const newManagerCode = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
        await updateDoc(doc(db, 'agencies', currentAgency.key, 'settings', 'agency_info'), {
          'roleCodes.Manager': newManagerCode
        });
        console.log('Manager code added to existing agency');
      } catch (error) {
        console.error('Error adding Manager code:', error);
      }
    }
  };

  // Listen to users collection in Firestore for current agency
  useEffect(() => {
    if (!currentAgency) return;

    // Ensure Manager code exists
    ensureManagerCode();

    const agencyUsersCollection = getAgencyCollection('authorized_users');
    if (!agencyUsersCollection) return;

    const unsubscribe = onSnapshot(agencyUsersCollection, (snapshot) => {
      const usersData = [];
      snapshot.forEach((doc) => {
        const userData = doc.data();
        usersData.push({ 
          email: userData.email || doc.id.replace(/_/g, '@'), 
          ...userData 
        });
      });
      
      // Sort users by role hierarchy and then by email
      const roleOrder = { admin: 0, manager: 1, Manager: 1, QA: 2, L1: 3, L2: 4 };
      usersData.sort((a, b) => {
        const roleCompare = (roleOrder[a.role] || 5) - (roleOrder[b.role] || 5);
        if (roleCompare !== 0) return roleCompare;
        return a.email.localeCompare(b.email);
      });
      
      setUsers(usersData);
    });

    return () => unsubscribe();
  }, [currentAgency, getAgencyCollection]);

  const handleRevokeAccess = async (userEmail) => {
    if (userEmail === currentAgency?.adminEmail) {
      alert("Cannot remove agency administrator");
      return;
    }

    if (!window.confirm(`Remove ${userEmail}?`)) {
      return;
    }

    try {
      const userDocId = userEmail.replace(/[^a-zA-Z0-9]/g, '_');
      await deleteDoc(doc(db, 'agencies', currentAgency.key, 'authorized_users', userDocId));
      setSuccess(`${userEmail} removed`);
      setTimeout(() => setSuccess(""), 2000);
    } catch (err) {
      alert("Error removing user");
    }
  };

  const getRoleClass = (role) => {
    switch (role) {
      case 'admin': return 'role-badge role-admin';
      case 'manager': return 'role-badge role-manager';
      case 'Manager': return 'role-badge role-manager';
      case 'QA': return 'role-badge role-qa';
      case 'L1': return 'role-badge role-l1';
      case 'L2': return 'role-badge role-l2';
      default: return 'role-badge';
    }
  };

  const copyRoleCode = (role) => {
    const code = currentAgency?.roleCodes?.[role];
    if (code) {
      navigator.clipboard.writeText(code).then(() => {
        setModalSuccess("Copied!");
        setTimeout(() => setModalSuccess(""), 2000);
      });
    }
  };



  return (
    <div className="manage-users-container">
      {success && <div className="success-message">{success}</div>}

      <div className="users-list-section">
        <div className="users-list-header">
          <h3 className="users-list-title">
            <Group />
            Users
            <div className="users-count-badge">
              {users.length}
            </div>
          </h3>
          <button 
            onClick={() => setShowInviteModal(true)}
            className="invite-user-btn"
          >
            <Link />
            <span>Invite User</span>
          </button>
        </div>
        <div className="users-table-container">
          <table className="users-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Role</th>
                <th>Status</th>
                <th>Added</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan="5" className="empty-state">
                    <div className="empty-state-text">No users found</div>
                  </td>
                </tr>
              ) : (
                users.map((userItem) => (
                  <tr key={userItem.email}>
                    <td>
                      <div className="user-email">
                        {userItem.email}
                        {userItem.email === currentAgency?.adminEmail && (
                          <span className="protected-badge"><Lock style={{scale: 0.7}}/></span>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className={getRoleClass(userItem.role)}>
                        {userItem.role}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge status-${userItem.status}`}>
                        {userItem.status}
                      </span>
                    </td>
                    <td>
                      <span className="added-date">
                        {userItem.addedAt && userItem.addedAt.toDate ? 
                          userItem.addedAt.toDate().toLocaleDateString() : 
                          userItem.addedAt ? 
                            new Date(userItem.addedAt).toLocaleDateString() : 
                            'N/A'
                        }
                      </span>
                    </td>
                    <td>
                      {userItem.email !== currentAgency?.adminEmail ? (
                        <button
                          onClick={() => handleRevokeAccess(userItem.email)}
                          className="revoke-btn"
                        >
                          Remove
                        </button>
                      ) : (
                        <span className="cannot-revoke">Admin</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Invite User Popup */}
      {showInviteModal && createPortal(
        <div className="invite-popup__overlay" onClick={() => setShowInviteModal(false)}>
          <div className="invite-popup__content" onClick={(e) => e.stopPropagation()}>
            <div className="invite-popup__header">
              <h3 className="invite-popup__title">Invite Users</h3>
              <button 
                className="invite-popup__close"
                onClick={() => setShowInviteModal(false)}
              >
                <Close />
              </button>
            </div>
            <div className="invite-popup__body">
              <div className="role-codes-section">
                <p className="invite-popup__description">
                  Share these invitation codes with users to grant them access to your agency. 
                </p>
                
                <div className="role-codes-grid">
                  {['Manager', 'L1', 'L2', 'QA'].map((role) => (
                    <div key={role} className="role-code-card">
                      <div className="role-code-header">
                        <span className={`role-badge role-${role.toLowerCase()}`}>{role}</span>
                      </div>
                      
                      <div className="role-code-input-group">
                        <input 
                          type="text" 
                          value={currentAgency?.roleCodes?.[role] || 'Not generated'} 
                          readOnly 
                          className="role-code-input"
                        />
                        <button 
                          onClick={() => copyRoleCode(role)}
                          className="copy-code-button"
                          disabled={!currentAgency?.roleCodes?.[role]}
                        >
                          <ContentCopy style={{fontSize: '16px'}} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                {modalSuccess && <div className="modal-success-message">{modalSuccess}</div>}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default ManageUsers;